import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnDestroy,
    OnInit,
    ViewChildren,
    QueryList
} from '@angular/core';
import { finalize, Subject, takeUntil, take } from 'rxjs';
import { TextareaComponent } from '@epd/pattern-library';

import { KeyCode, ChatRole } from '@app/enums';
import { ChatMessage, ChatBotResponse } from '@app/interfaces';
import { StreamingLeftNavigationService } from '../../navigation/left-navigation.service';
import { ActiveConversationService } from '../active-conversation.service';
import { AuthClientService } from '../../auth-client/auth-client.service';
import { ConversationHistoryStorageService } from '../../conversation-history/conversation-history-storage.service';
import { RealtimeSessionStreamService } from '../../realtime-session-stream/realtime-session-stream.service';

@Component({
    selector: 'craig-streaming-user-input',
    templateUrl: './user-input.component.html',
    styleUrls: ['./user-input.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class UserInputComponent implements OnInit, AfterViewInit, OnDestroy {
    userQuery: string = '';
    isSubmitInProgress: boolean = false;

    @ViewChildren('queryInput') queryInputList!: QueryList<TextareaComponent>;

    private readonly componentDestroyed$: Subject<boolean> = new Subject();
    private readonly queryAborted$: Subject<boolean> = new Subject();
    private queryInputComponent?: TextareaComponent;

    constructor(
        private readonly leftNavigationService: StreamingLeftNavigationService,
        private readonly activeConversationService: ActiveConversationService,
        private readonly authClientService: AuthClientService,
        private readonly conversationStorageService: ConversationHistoryStorageService,
        private readonly changeDetectorRef: ChangeDetectorRef,
        private readonly realtimeStreamService: RealtimeSessionStreamService
    ) {}

    ngOnInit(): void {
        // Subscribe to streaming state for submit button state
        this.realtimeStreamService.isStreaming$
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((streaming: boolean) => {
                this.isSubmitInProgress = streaming;
                this.changeDetectorRef.detectChanges();
            });
    }

    ngAfterViewInit(): void {
        this.queryInputList.changes.subscribe(() => {
            this.queryInputComponent = this.queryInputList.first;
        });
    }

    ngOnDestroy(): void {
        this.componentDestroyed$.next(true);
    }

    onUserQueryKeydown(e: Event): void {
        const { key, code, shiftKey } = e as KeyboardEvent;
        const keyCode: string = key || code;

        if (keyCode === KeyCode.Enter && !shiftKey) {
            e.preventDefault();

            if (!this.isSubmitInProgress) {
                this.submit();
            }
        }
    }

    onUserQueryChange(value: string): void {
        this.userQuery = value;
    }

    handlePromptUpdate(newSamplePromptText: string): void {
        this.userQuery = newSamplePromptText;
        this.focusQueryInput();
    }

    submit(): void {
        if (!!this.userQuery.trim() && !this.isSubmitInProgress) {
            // console.log('🚀 User submitting message:', this.userQuery);

            // Prevent double submissions - check if already streaming
            if (this.isSubmitInProgress) {
                // console.log('⚠️ Submit already in progress, ignoring duplicate submit');
                return;
            }

            // Ensure we have a chat session
            if (this.conversationStorageService.getCurrentConversationId() === -1) {
                // console.log('🆕 No active conversation session, creating new one');
                this.conversationStorageService.addNewConversation();

                // Track conversation start for funnel analysis
                this.trackConversationStart();
            }

            // Use the new realtime streaming service instead of auth client
            this.submitWithRealtimeStream();
        }

        // Clearing of the conversation input can be done even if the response is bad.
        this.clearUserQuery();
    }

    private async submitWithRealtimeStream(): Promise<void> {
        try {
            // Track question asked for funnel analysis
            this.trackQuestionAsked();

            // Add the user message to the conversation state
            const userMessage = this.realtimeStreamService.addUserMessage(this.userQuery);

            // Get the full conversation including the new message
            // conversationForAgent$ has the messages ready for the API
            let conversationToSend: ChatMessage[] = [];
            this.realtimeStreamService.conversationForAgent$.pipe(take(1)).subscribe((messages) => {
                conversationToSend = [...messages];
            });

            // console.log('📤 Sending conversation with', conversationToSend.length, 'messages');

            // Start the stream with the full conversation context
            await this.realtimeStreamService.startConversationStream(conversationToSend);

            // console.log('🎉 Realtime stream started successfully');
        } catch (error) {
            console.error('❌ Failed to start realtime stream:', error);
            // Error handling is now managed by realtime stream service
        }
    }

    private generateRequestId(): string {
        return `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }

    clearUserQuery(): void {
        this.userQuery = '';
        this.changeDetectorRef.detectChanges();
    }

    abortQuery(): void {
        this.queryAborted$.next(true);
        this.realtimeStreamService.abortStream();
    }

    private focusQueryInput(): void {
        if (this.queryInputComponent) {
            this.queryInputComponent.embeddedInput.nativeElement.focus();
        }
    }

    /**
     * Track conversation start event for New Relic funnel analysis
     */
    private trackConversationStart(): void {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const newrelic = (window as any).newrelic;

        if (newrelic && newrelic.addPageAction) {
            const conversationId = this.conversationStorageService.getCurrentConversationId();

            newrelic.addPageAction('conversation_started', {
                conversationId: conversationId,
                timestamp: new Date().toISOString(),
                sessionId: this.generateSessionId(),
                userAgent: navigator.userAgent,
                viewportSize: `${window.innerWidth}x${window.innerHeight}`
            });
        }
    }

    /**
     * Track question asked event for New Relic funnel analysis
     */
    private trackQuestionAsked(): void {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const newrelic = (window as any).newrelic;

        if (newrelic && newrelic.addPageAction) {
            const conversationId = this.conversationStorageService.getCurrentConversationId();
            const questionLength = this.userQuery.trim().length;
            const wordCount = this.userQuery
                .trim()
                .split(/\s+/)
                .filter((word) => word.length > 0).length;

            newrelic.addPageAction('question_asked', {
                conversationId: conversationId,
                questionLength: questionLength,
                wordCount: wordCount,
                timestamp: new Date().toISOString(),
                sessionId: this.generateSessionId(),
                // Capture the actual question content for analysis
                questionContent: this.userQuery.trim(),
                hasSpecialChars: /[!@#$%^&*(),.?":{}|<>]/.test(this.userQuery),
                isQuestion: this.userQuery.trim().endsWith('?'),
                startsWithWhat: this.userQuery.trim().toLowerCase().startsWith('what'),
                startsWithHow: this.userQuery.trim().toLowerCase().startsWith('how'),
                startsWithWhy: this.userQuery.trim().toLowerCase().startsWith('why')
            });
        }
    }

    /**
     * Generate a session ID for tracking user sessions
     */
    private generateSessionId(): string {
        // Try to get existing session ID from localStorage, or create a new one
        const existingSessionId = localStorage.getItem('craig_session_id');
        if (existingSessionId) {
            return existingSessionId;
        }

        const sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        localStorage.setItem('craig_session_id', sessionId);
        return sessionId;
    }
}
